import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Node {
  id: string;
  x: number;
  y: number;
  label: string;
  status: 'complete' | 'running' | 'pending' | 'attention';
}

interface Connection {
  from: string;
  to: string;
}

const statusColors = {
  complete: {
    fill: '#10b981',
    stroke: '#059669',
    glow: 'rgba(16, 185, 129, 0.4)'
  },
  running: {
    fill: '#3b82f6',
    stroke: '#2563eb',
    glow: 'rgba(59, 130, 246, 0.5)'
  },
  pending: {
    fill: '#4b5563',
    stroke: '#374151',
    glow: 'transparent'
  },
  attention: {
    fill: '#f59e0b',
    stroke: '#d97706',
    glow: 'rgba(245, 158, 11, 0.5)'
  }
};

// Animation sequence showing multi-agent orchestration
const animationStates: Node[][] = [
  // State 0: Initial prompt
  [
    { id: 'prompt', x: 80, y: 150, label: 'Prompt', status: 'complete' },
  ],
  // State 1: Splits into 3 parallel agents
  [
    { id: 'prompt', x: 80, y: 150, label: 'Prompt', status: 'complete' },
    { id: 'plan', x: 220, y: 60, label: 'Plan', status: 'running' },
    { id: 'search', x: 220, y: 150, label: 'Search', status: 'running' },
    { id: 'analyze', x: 220, y: 240, label: 'Analyze', status: 'running' },
  ],
  // State 2: Two complete, one needs attention
  [
    { id: 'prompt', x: 80, y: 150, label: 'Prompt', status: 'complete' },
    { id: 'plan', x: 220, y: 60, label: 'Plan', status: 'complete' },
    { id: 'search', x: 220, y: 150, label: 'Search', status: 'complete' },
    { id: 'analyze', x: 220, y: 240, label: 'Approve?', status: 'attention' },
  ],
  // State 3: After approval, execute phase
  [
    { id: 'prompt', x: 80, y: 150, label: 'Prompt', status: 'complete' },
    { id: 'plan', x: 220, y: 60, label: 'Plan', status: 'complete' },
    { id: 'search', x: 220, y: 150, label: 'Search', status: 'complete' },
    { id: 'analyze', x: 220, y: 240, label: 'Analyze', status: 'complete' },
    { id: 'exec1', x: 360, y: 90, label: 'Execute', status: 'running' },
    { id: 'exec2', x: 360, y: 210, label: 'Execute', status: 'running' },
  ],
  // State 4: Converge to PR
  [
    { id: 'prompt', x: 80, y: 150, label: 'Prompt', status: 'complete' },
    { id: 'plan', x: 220, y: 60, label: 'Plan', status: 'complete' },
    { id: 'search', x: 220, y: 150, label: 'Search', status: 'complete' },
    { id: 'analyze', x: 220, y: 240, label: 'Analyze', status: 'complete' },
    { id: 'exec1', x: 360, y: 90, label: 'Execute', status: 'complete' },
    { id: 'exec2', x: 360, y: 210, label: 'Execute', status: 'complete' },
    { id: 'pr', x: 480, y: 150, label: 'PR', status: 'complete' },
  ],
];

const connectionStates: Connection[][] = [
  [],
  [
    { from: 'prompt', to: 'plan' },
    { from: 'prompt', to: 'search' },
    { from: 'prompt', to: 'analyze' },
  ],
  [
    { from: 'prompt', to: 'plan' },
    { from: 'prompt', to: 'search' },
    { from: 'prompt', to: 'analyze' },
  ],
  [
    { from: 'prompt', to: 'plan' },
    { from: 'prompt', to: 'search' },
    { from: 'prompt', to: 'analyze' },
    { from: 'plan', to: 'exec1' },
    { from: 'search', to: 'exec1' },
    { from: 'analyze', to: 'exec2' },
  ],
  [
    { from: 'prompt', to: 'plan' },
    { from: 'prompt', to: 'search' },
    { from: 'prompt', to: 'analyze' },
    { from: 'plan', to: 'exec1' },
    { from: 'search', to: 'exec1' },
    { from: 'analyze', to: 'exec2' },
    { from: 'exec1', to: 'pr' },
    { from: 'exec2', to: 'pr' },
  ],
];

export function AnimatedDAG() {
  const [stateIndex, setStateIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStateIndex(prev => (prev + 1) % animationStates.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const nodes = animationStates[stateIndex];
  const connections = connectionStates[stateIndex];

  const getNodePosition = (id: string) => {
    const node = nodes.find(n => n.id === id);
    return node ? { x: node.x, y: node.y } : { x: 0, y: 0 };
  };

  return (
    <div className="relative w-full h-full">
      <svg viewBox="0 0 560 300" className="w-full h-full" style={{
        filter: 'drop-shadow(0 0 40px rgba(59, 130, 246, 0.15))'
      }}>
        <defs>
          <filter id="glow-v2" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="lineGradient-v2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="attentionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>

        {/* Connection lines */}
        {connections.map((conn, i) => {
          const from = getNodePosition(conn.from);
          const to = getNodePosition(conn.to);
          const midX = (from.x + to.x) / 2;
          return (
            <motion.path
              key={`${conn.from}-${conn.to}`}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              d={`M ${from.x} ${from.y} Q ${midX} ${from.y} ${midX} ${(from.y + to.y) / 2} Q ${midX} ${to.y} ${to.x} ${to.y}`}
              fill="none"
              stroke="url(#lineGradient-v2)"
              strokeWidth="2"
              strokeDasharray="6 3"
            />
          );
        })}

        {/* Nodes */}
        {nodes.map((node, i) => {
          const colors = statusColors[node.status];
          return (
            <motion.g
              key={node.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: i * 0.1
              }}
            >
              {/* Glow effect */}
              <motion.circle
                cx={node.x}
                cy={node.y}
                r="32"
                fill={colors.glow}
                filter="url(#glow-v2)"
                animate={node.status === 'attention' ? {
                  r: [32, 38, 32],
                  opacity: [0.5, 0.8, 0.5]
                } : {}}
                transition={{ repeat: Infinity, duration: 1.5 }}
              />

              {/* Node circle */}
              <motion.circle
                cx={node.x}
                cy={node.y}
                r="26"
                fill={node.status === 'attention' ? 'url(#attentionGradient)' : colors.fill}
                stroke={colors.stroke}
                strokeWidth="2"
                animate={node.status === 'running' ? {
                  scale: [1, 1.05, 1],
                } : node.status === 'attention' ? {
                  scale: [1, 1.1, 1],
                } : {}}
                transition={{ repeat: Infinity, duration: 1.2 }}
              />

              {/* Progress ring for running nodes */}
              {node.status === 'running' && (
                <motion.circle
                  cx={node.x}
                  cy={node.y}
                  r="26"
                  fill="none"
                  stroke="#60a5fa"
                  strokeWidth="3"
                  strokeDasharray="164"
                  strokeLinecap="round"
                  initial={{ strokeDashoffset: 164 }}
                  animate={{ strokeDashoffset: 0 }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ transform: `rotate(-90deg)`, transformOrigin: `${node.x}px ${node.y}px` }}
                />
              )}

              {/* Label */}
              <text
                x={node.x}
                y={node.y + 4}
                textAnchor="middle"
                fill="white"
                fontSize="11"
                fontWeight="600"
                fontFamily="Inter, sans-serif"
              >
                {node.label}
              </text>
            </motion.g>
          );
        })}

        {/* Agent count indicator */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <rect x="440" y="10" width="100" height="28" rx="14" fill="rgba(59, 130, 246, 0.2)" stroke="rgba(59, 130, 246, 0.4)" />
          <text x="490" y="29" textAnchor="middle" fill="#60a5fa" fontSize="12" fontWeight="500" fontFamily="Inter, sans-serif">
            {nodes.filter(n => n.status === 'running').length} agents active
          </text>
        </motion.g>
      </svg>
    </div>
  );
}
